import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Search, Car, Play, Clock, Eye, Filter, X, ChevronDown,
  RefreshCw, Settings, Tv, MonitorPlay, Wrench, Cog,
  Disc3, Gauge, Thermometer, Droplets, Zap, Hammer,
  ArrowUpDown, Loader2, ExternalLink, Youtube, CheckCircle2,
  AlertCircle, Video, ChevronRight, Tag, Heart, Bookmark,
  Plus, Trash2, Star, Lock, Hash, Globe, Shield
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYSTEM_FILTERS = [
  { id: "motore", label: "Motore", icon: Cog },
  { id: "freni", label: "Freni", icon: Disc3 },
  { id: "distribuzione", label: "Distribuzione", icon: ArrowUpDown },
  { id: "sospensioni", label: "Sospensioni", icon: Gauge },
  { id: "cambio", label: "Cambio", icon: Settings },
  { id: "diagnosi", label: "Diagnosi", icon: Zap },
  { id: "egr", label: "EGR", icon: Wrench },
  { id: "dpf", label: "DPF/FAP", icon: Filter },
  { id: "adblue", label: "AdBlue", icon: Droplets },
  { id: "climatizzazione", label: "Clima", icon: Thermometer },
  { id: "manutenzione", label: "Manutenzione", icon: Hammer },
];

const INTERVENTION_TYPES = [
  { id: "", label: "Tutti" }, { id: "diagnosi", label: "Diagnosi" },
  { id: "manutenzione", label: "Manutenzione" }, { id: "sostituzione", label: "Sostituzione" },
  { id: "smontaggio", label: "Smontaggio" }, { id: "riparazione", label: "Riparazione" },
  { id: "test", label: "Test" },
];

const DURATION_FILTERS = [
  { id: "", label: "Tutte" }, { id: "breve", label: "< 5 min" },
  { id: "medio", label: "5-20 min" }, { id: "lungo", label: "> 20 min" },
];

// ── Video Card ──────────────────────────────────────────────────
function VideoCard({ video, onPlay, isFavorite, onToggleFavorite, vehicleBrand, vehicleModel }) {
  const score = video.score || 0;
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-600 transition-all cursor-pointer group overflow-hidden" data-testid={`video-card-${video.video_id}`}>
      <div className="relative" onClick={() => onPlay(video)}>
        <img src={video.thumbnail || `https://i.ytimg.com/vi/${video.video_id}/hqdefault.jpg`} alt={video.title} className="w-full aspect-video object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
            <Play className="w-7 h-7 text-white ml-1" />
          </div>
        </div>
        {video.duration_formatted && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded font-mono">{video.duration_formatted}</span>
        )}
        {score >= 50 && (
          <span className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Consigliato</span>
        )}
      </div>
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-medium text-white line-clamp-2 leading-snug group-hover:text-orange-400 transition-colors flex-1" onClick={() => onPlay(video)}>
            {video.title}
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(video); }}
            className={`p-1.5 rounded-lg flex-shrink-0 transition-all ${isFavorite ? "text-red-400 bg-red-500/15 hover:bg-red-500/25" : "text-zinc-600 hover:text-red-400 hover:bg-zinc-800"}`}
            data-testid={`fav-btn-${video.video_id}`}
            title={isFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Youtube className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs text-zinc-500 truncate">{video.channel_name}</span>
        </div>
        {video.view_count > 0 && (
          <span className="text-[11px] text-zinc-600 mt-1 block">{Number(video.view_count).toLocaleString("it-IT")} visualizzazioni</span>
        )}
      </CardContent>
    </Card>
  );
}

// ── Player Modal ────────────────────────────────────────────────
function VideoPlayer({ video, onClose, isFavorite, onToggleFavorite }) {
  if (!video) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="video-player-modal">
      <div className="w-full max-w-5xl bg-zinc-900 rounded-xl overflow-hidden border border-zinc-700 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-zinc-800">
          <h3 className="text-white font-medium text-sm truncate pr-4">{video.title}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToggleFavorite(video)}
              className={`p-1.5 rounded-lg transition-all ${isFavorite ? "text-red-400 bg-red-500/15" : "text-zinc-500 hover:text-red-400"}`}
              data-testid="player-fav-btn"
            >
              <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors" data-testid="close-player-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="aspect-video">
          <iframe src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0&modestbranding=1`} title={video.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Youtube className="w-5 h-5 text-red-500" />
              <span className="text-sm text-zinc-300">{video.channel_name}</span>
              {video.duration_formatted && <span className="text-xs text-zinc-500">Durata: {video.duration_formatted}</span>}
            </div>
            <a href={`https://www.youtube.com/watch?v=${video.video_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> YouTube
            </a>
          </div>
          {video.description && <p className="text-xs text-zinc-500 mt-3 line-clamp-3">{video.description}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Channel Management Panel ────────────────────────────────────
function ChannelManager({ isAdmin, onClose }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newChannelId, setNewChannelId] = useState("");
  const [newChannelName, setNewChannelName] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => { fetchChannels(); }, []);

  const fetchChannels = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/channels`);
      if (res.ok) setChannels(await res.json());
    } catch (e) { console.error('VideoRiparazioni fetch error:', e); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newChannelId.trim() || !newChannelName.trim()) { toast.error("Inserisci ID canale e nome"); return; }
    setAdding(true);
    try {
      const res = await authFetch(`${API}/video-repairs/channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: newChannelId.trim(), name: newChannelName.trim(), priority: 5 }),
      });
      if (res.ok) {
        toast.success("Canale aggiunto");
        setNewChannelId(""); setNewChannelName("");
        fetchChannels();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'aggiunta");
      }
    } catch (e) { toast.error("Errore di connessione"); }
    setAdding(false);
  };

  const handleToggle = async (channelId, enabled) => {
    try {
      await authFetch(`${API}/video-repairs/channels/${channelId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !enabled }),
      });
      setChannels(prev => prev.map(c => c.channel_id === channelId ? { ...c, enabled: !enabled } : c));
    } catch (e) { toast.error("Errore"); }
  };

  const handleDelete = async (channelId) => {
    if (!window.confirm("Rimuovere questo canale?")) return;
    try {
      await authFetch(`${API}/video-repairs/channels/${channelId}`, { method: "DELETE" });
      setChannels(prev => prev.filter(c => c.channel_id !== channelId));
      toast.success("Canale rimosso");
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="channel-manager-modal">
      <div className="w-full max-w-2xl bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-500" /> Gestione Canali YouTube
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Add new channel */}
          {isAdmin && (
            <Card className="bg-zinc-800/50 border-zinc-700">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm text-zinc-400 font-medium">Aggiungi Canale</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 block mb-1">Channel ID</label>
                    <Input placeholder="UC..." value={newChannelId} onChange={e => setNewChannelId(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="new-channel-id-input" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 block mb-1">Nome Canale</label>
                    <Input placeholder="Nome del canale" value={newChannelName} onChange={e => setNewChannelName(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="new-channel-name-input" />
                  </div>
                </div>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleAdd} disabled={adding} data-testid="add-channel-btn">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Aggiungi
                </Button>
                <p className="text-[11px] text-zinc-600">Trova il Channel ID nella URL del canale YouTube: youtube.com/channel/<strong>UC...</strong></p>
              </CardContent>
            </Card>
          )}

          {/* Channel list */}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
          ) : (
            <div className="space-y-2">
              {channels.map(ch => (
                <div key={ch.channel_id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${ch.enabled ? "bg-zinc-800/50 border-zinc-700" : "bg-zinc-900/50 border-zinc-800 opacity-60"}`} data-testid={`channel-row-${ch.channel_id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Youtube className={`w-5 h-5 flex-shrink-0 ${ch.enabled ? "text-red-500" : "text-zinc-600"}`} />
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{ch.name}</p>
                      <p className="text-[11px] text-zinc-600 font-mono truncate">{ch.channel_id}</p>
                    </div>
                    <span className="text-[11px] text-zinc-600 flex-shrink-0">{ch.video_count || 0} video</span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 ml-3">
                      <Switch checked={ch.enabled} onCheckedChange={() => handleToggle(ch.channel_id, ch.enabled)} data-testid={`channel-toggle-${ch.channel_id}`} />
                      <button onClick={() => handleDelete(ch.channel_id)} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-600 hover:text-red-400 transition-colors" data-testid={`channel-delete-${ch.channel_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────
export default function VideoRiparazioni({ user, preloadedVehicle }) {
  const [plate, setPlate] = useState("");
  const [vehicleData, setVehicleData] = useState(preloadedVehicle || null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [recentVideos, setRecentVideos] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeSystem, setActiveSystem] = useState("");
  const [intervention, setIntervention] = useState("");
  const [durationFilter, setDurationFilter] = useState("");
  const [playingVideo, setPlayingVideo] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [searchSource, setSearchSource] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showChannelManager, setShowChannelManager] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [moduleLoading, setModuleLoading] = useState(true);
  const [freeQuery, setFreeQuery] = useState("");
  const [freeSearchActive, setFreeSearchActive] = useState(false);
  const searchRef = useRef(null);

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "staff";

  // Check module status & load favorites
  useEffect(() => {
    checkModuleStatus();
    fetchFavoriteIds();
    fetchRecentVideos();
  }, []);

  useEffect(() => {
    if (preloadedVehicle) { setVehicleData(preloadedVehicle); searchVideos(preloadedVehicle); }
  }, [preloadedVehicle]);

  const checkModuleStatus = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/module-status`);
      if (res.ok) { const d = await res.json(); setModuleEnabled(d.enabled); }
    } catch (e) { console.error('VideoRiparazioni module-status error:', e); }
    setModuleLoading(false);
  };

  const fetchFavoriteIds = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/favorites/ids`);
      if (res.ok) { const ids = await res.json(); setFavoriteIds(new Set(ids)); }
    } catch (e) { console.error('VideoRiparazioni fetch error:', e); }
  };

  const fetchFavorites = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/favorites`);
      if (res.ok) setFavorites(await res.json());
    } catch (e) { console.error('VideoRiparazioni fetch error:', e); }
  };

  const fetchRecentVideos = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/recent?limit=8`);
      if (res.ok) setRecentVideos(await res.json());
    } catch (e) { console.error('VideoRiparazioni fetch error:', e); }
  };

  const toggleFavorite = async (video) => {
    const isFav = favoriteIds.has(video.video_id);
    if (isFav) {
      try {
        const res = await authFetch(`${API}/video-repairs/favorites/${video.video_id}`, { method: "DELETE" });
        if (res.ok) {
          setFavoriteIds(prev => { const n = new Set(prev); n.delete(video.video_id); return n; });
          setFavorites(prev => prev.filter(f => f.video_id !== video.video_id));
          toast.success("Rimosso dai preferiti");
        }
      } catch (e) { toast.error("Errore"); }
    } else {
      try {
        const res = await authFetch(`${API}/video-repairs/favorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            video_id: video.video_id, title: video.title, channel_name: video.channel_name,
            thumbnail: video.thumbnail || `https://i.ytimg.com/vi/${video.video_id}/hqdefault.jpg`,
            duration_formatted: video.duration_formatted || "",
            vehicle_brand: vehicleData?.brand || "", vehicle_model: vehicleData?.model || "",
          }),
        });
        if (res.ok) {
          setFavoriteIds(prev => new Set([...prev, video.video_id]));
          toast.success("Aggiunto ai preferiti");
        }
      } catch (e) { toast.error("Errore"); }
    }
  };

  const handleLookup = async () => {
    const normalized = plate.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (!normalized || normalized.length < 5) { toast.error("Inserisci una targa valida"); return; }
    setLookupLoading(true);
    try {
      const res = await authFetch(`${API}/vehicles/lookup/${normalized}`);
      if (res.ok) { const data = await res.json(); setVehicleData(data); setFreeSearchActive(false); toast.success(`Veicolo trovato: ${data.brand} ${data.model}`); searchVideos(data); }
      else { toast.error("Veicolo non trovato"); setVehicleData(null); }
    } catch (e) { toast.error("Errore nella ricerca"); }
    setLookupLoading(false);
  };

  const searchVideos = useCallback(async (vehicle, opts = {}) => {
    if (!vehicle) return;
    setSearchLoading(true); setPage(1);
    const params = new URLSearchParams({
      brand: vehicle.brand || "", model: vehicle.model || "", engine: vehicle.modelDetail || "",
      fuel: vehicle.fuelType || "", system_filter: opts.system ?? activeSystem,
      intervention: opts.intervention ?? intervention, duration_filter: opts.duration ?? durationFilter,
      page: "1", limit: "20",
    });
    try {
      const res = await authFetch(`${API}/video-repairs/search?${params}`);
      if (res.ok) { const d = await res.json(); setVideos(d.videos || []); setTotalResults(d.total || 0); setSearchSource(d.source || ""); }
      else { setVideos([]); toast.error("Errore nella ricerca video"); }
    } catch (e) { toast.error("Errore di connessione"); }
    setSearchLoading(false);
  }, [activeSystem, intervention, durationFilter]);

  const handleSystemFilter = (systemId) => {
    const ns = activeSystem === systemId ? "" : systemId;
    setActiveSystem(ns);
    if (vehicleData) searchVideos(vehicleData, { system: ns });
  };

  const handleInterventionChange = (val) => { setIntervention(val); if (vehicleData) searchVideos(vehicleData, { intervention: val }); };
  const handleDurationChange = (val) => { setDurationFilter(val); if (vehicleData) searchVideos(vehicleData, { duration: val }); };

  const handleRefreshFeeds = async () => {
    try {
      const res = await authFetch(`${API}/video-repairs/refresh-feeds`, { method: "POST" });
      if (res.ok) { const d = await res.json(); toast.success(`Feed aggiornati: ${d.new_videos} nuovi video da ${d.channels_processed} canali`); fetchRecentVideos(); }
    } catch (e) { toast.error("Errore aggiornamento feed"); }
  };

  const handleFreeSearch = async () => {
    if (!freeQuery.trim() || freeQuery.trim().length < 2) { toast.error("Inserisci almeno 2 caratteri"); return; }
    setSearchLoading(true); setFreeSearchActive(true); setVehicleData(null); setActiveSystem("");
    const params = new URLSearchParams({ q: freeQuery.trim(), duration_filter: durationFilter, page: "1", limit: "20" });
    try {
      const res = await authFetch(`${API}/video-repairs/search-free?${params}`);
      if (res.ok) { const d = await res.json(); setVideos(d.videos || []); setTotalResults(d.total || 0); setSearchSource(d.source || ""); }
      else { setVideos([]); toast.error("Errore nella ricerca"); }
    } catch (e) { toast.error("Errore di connessione"); }
    setSearchLoading(false);
  };

  const clearFreeSearch = () => { setFreeSearchActive(false); setFreeQuery(""); setVideos([]); setTotalResults(0); };

  const handleShowFavorites = () => { setShowFavorites(!showFavorites); if (!showFavorites) fetchFavorites(); };

  // Module disabled
  if (moduleLoading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;
  if (!moduleEnabled) return (
    <div className="flex items-center justify-center h-64 p-6">
      <Card className="bg-zinc-900 border-zinc-800 max-w-md">
        <CardContent className="p-8 text-center">
          <Lock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <h2 className="text-white font-bold text-lg">Modulo Disabilitato</h2>
          <p className="text-zinc-500 text-sm mt-2">Il modulo Video Riparazioni non e attivo per la tua officina. Contatta l'amministratore per abilitarlo.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-5 p-6" data-testid="video-riparazioni-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30">
            <MonitorPlay className="w-7 h-7 text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Video Riparazioni Automotive</h1>
            <p className="text-zinc-400 mt-0.5">Libreria tecnica video per diagnosi e riparazioni</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFavorites ? "default" : "outline"}
            className={showFavorites ? "bg-red-600 hover:bg-red-700 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}
            size="sm" onClick={handleShowFavorites} data-testid="show-favorites-btn"
          >
            <Heart className={`w-4 h-4 mr-1.5 ${showFavorites ? "fill-current" : ""}`} />
            Preferiti {favoriteIds.size > 0 && `(${favoriteIds.size})`}
          </Button>
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" size="sm" onClick={() => setShowChannelManager(true)} data-testid="manage-channels-btn">
            <Youtube className="w-4 h-4 mr-1.5" />
            Canali
          </Button>
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" size="sm" onClick={handleRefreshFeeds} data-testid="refresh-feeds-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Favorites Section */}
      {showFavorites && (
        <Card className="bg-zinc-900 border-red-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Heart className="w-5 h-5 text-red-400 fill-current" /> I Miei Video Preferiti ({favorites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {favorites.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-6">Nessun video preferito. Clicca il cuore su un video per salvarlo.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="favorites-grid">
                {favorites.map(v => (
                  <VideoCard key={v.video_id} video={v} onPlay={setPlayingVideo} isFavorite={true} onToggleFavorite={toggleFavorite} vehicleBrand={v.vehicle_brand} vehicleModel={v.vehicle_model} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search bar */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1.5 block">Ricerca veicolo per targa</label>
              <div className="relative">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input ref={searchRef} placeholder="Inserisci targa (es. AB123CD)" value={plate}
                  onChange={e => { setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")); if (freeSearchActive) clearFreeSearch(); }}
                  onKeyDown={e => e.key === "Enter" && handleLookup()}
                  className="pl-11 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 h-11 text-base font-mono tracking-wider"
                  maxLength={10} data-testid="video-plate-input" />
              </div>
            </div>
            <Button className="bg-red-600 hover:bg-red-700 text-white h-11 px-6" onClick={handleLookup} disabled={lookupLoading || plate.length < 5} data-testid="video-plate-search-btn">
              {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Cerca Veicolo
            </Button>
          </div>

          {/* Separator */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-zinc-800"></div>
            <span className="text-xs text-zinc-600 font-medium">oppure</span>
            <div className="flex-1 border-t border-zinc-800"></div>
          </div>

          {/* Free text search */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1.5 block">Ricerca libera video</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input placeholder="Es: cambio olio Fiat Panda, sostituzione freni, diagnosi centralina..."
                  value={freeQuery}
                  onChange={e => setFreeQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleFreeSearch()}
                  className="pl-11 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 h-11 text-sm"
                  data-testid="free-search-input" />
              </div>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-6" onClick={handleFreeSearch} disabled={searchLoading || freeQuery.trim().length < 2} data-testid="free-search-btn">
              {searchLoading && freeSearchActive ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Cerca Video
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Free search active bar */}
      {freeSearchActive && !vehicleData && (
        <Card className="bg-gradient-to-r from-zinc-900 to-zinc-900/80 border-orange-500/30" data-testid="free-search-bar">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-orange-500/15"><Search className="w-5 h-5 text-orange-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Ricerca libera</p>
                <p className="text-white font-bold text-lg">"{freeQuery}"</p>
                <p className="text-xs text-zinc-500 mt-0.5">I risultati dai canali tecnici selezionati hanno priorita</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {totalResults > 0 && <span className="text-sm text-zinc-400"><span className="text-white font-bold">{totalResults}</span> video trovati</span>}
              <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-white" onClick={clearFreeSearch} data-testid="clear-free-search-btn">
                <X className="w-4 h-4 mr-1" /> Cancella
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle info bar */}
      {vehicleData && (
        <Card className="bg-gradient-to-r from-zinc-900 to-zinc-900/80 border-orange-500/30" data-testid="vehicle-info-bar">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-orange-500/15"><Car className="w-5 h-5 text-orange-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Video tecnici per</p>
                <p className="text-white font-bold text-lg">
                  {vehicleData.brand} {vehicleData.model}
                  {vehicleData.modelDetail && <span className="text-zinc-400 font-normal text-sm ml-2">{vehicleData.modelDetail}</span>}
                </p>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
                  {vehicleData.fuelType && <span>{vehicleData.fuelType}</span>}
                  {vehicleData.cubicCapacity > 0 && <span>{vehicleData.cubicCapacity}cc</span>}
                  {vehicleData.power_kw > 0 && <span>{vehicleData.power_kw}kW / {vehicleData.power_hp}CV</span>}
                  {vehicleData.buildYear > 0 && <span>Anno {vehicleData.buildYear}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {totalResults > 0 && <span className="text-sm text-zinc-400"><span className="text-white font-bold">{totalResults}</span> video trovati</span>}
              <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-white" onClick={() => { setVehicleData(null); setVideos([]); setPlate(""); setActiveSystem(""); }} data-testid="clear-vehicle-btn">
                <X className="w-4 h-4 mr-1" /> Cambia
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System filter chips */}
      {vehicleData && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-400">Filtra per sistema</h3>
            <Button variant="ghost" size="sm" className="text-xs text-zinc-500" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <Filter className="w-3.5 h-3.5 mr-1" />{showFilters ? "Nascondi filtri" : "Altri filtri"}
              <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="system-filters">
            {SYSTEM_FILTERS.map(sf => {
              const Icon = sf.icon; const isActive = activeSystem === sf.id;
              return (
                <button key={sf.id} onClick={() => handleSystemFilter(sf.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all border ${isActive ? "bg-red-600/20 border-red-500/50 text-red-400" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}
                  data-testid={`system-filter-${sf.id}`}><Icon className="w-3.5 h-3.5" />{sf.label}</button>
              );
            })}
          </div>
          {showFilters && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 font-medium block mb-2">Tipo Intervento</label>
                  <div className="flex flex-wrap gap-1.5">
                    {INTERVENTION_TYPES.map(it => (
                      <button key={it.id} onClick={() => handleInterventionChange(it.id)}
                        className={`px-3 py-1.5 rounded text-xs transition-all ${intervention === it.id ? "bg-orange-500/20 text-orange-400 border border-orange-500/40" : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white"}`}>{it.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 font-medium block mb-2">Durata Video</label>
                  <div className="flex flex-wrap gap-1.5">
                    {DURATION_FILTERS.map(df => (
                      <button key={df.id} onClick={() => handleDurationChange(df.id)}
                        className={`px-3 py-1.5 rounded text-xs transition-all ${durationFilter === df.id ? "bg-blue-500/20 text-blue-400 border border-blue-500/40" : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white"}`}>{df.label}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Loading */}
      {searchLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="text-center"><Loader2 className="w-10 h-10 animate-spin text-red-500 mx-auto mb-3" /><p className="text-zinc-400 text-sm">Ricerca video in corso...</p></div>
        </div>
      )}

      {/* Video results */}
      {!searchLoading && videos.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-red-400" />
              {activeSystem ? `Video ${SYSTEM_FILTERS.find(s => s.id === activeSystem)?.label || ""}` : "Video Consigliati"}
            </h2>
            {searchSource && <span className="text-[11px] text-zinc-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Fonte: {searchSource}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-grid">
            {videos.map(v => (
              <VideoCard key={v.video_id} video={v} onPlay={setPlayingVideo} isFavorite={favoriteIds.has(v.video_id)} onToggleFavorite={toggleFavorite} vehicleBrand={vehicleData?.brand} vehicleModel={vehicleData?.model} />
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {!searchLoading && vehicleData && videos.length === 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <h3 className="text-white font-medium">Nessun video trovato</h3>
            <p className="text-zinc-500 text-sm mt-1">Prova a modificare i filtri o cerca con un sistema diverso.</p>
            <Button variant="outline" className="mt-4 border-zinc-700 text-zinc-300" onClick={handleRefreshFeeds}>
              <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna archivio video
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Welcome / Recent (no vehicle) */}
      {!vehicleData && !searchLoading && !showFavorites && (
        <div className="space-y-5">
          <Card className="bg-gradient-to-br from-red-950/30 to-zinc-900 border-red-800/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <MonitorPlay className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Libreria Video Tecnica</h2>
              <p className="text-zinc-400 text-sm mt-2 max-w-lg mx-auto">
                Inserisci una targa per trovare video di riparazione e diagnosi specifici per il tuo veicolo.
                I video provengono da canali tecnici selezionati e verificati.
              </p>
              <div className="flex items-center justify-center gap-6 mt-5">
                {[
                  { icon: Search, text: "Cerca per targa" }, { icon: Filter, text: "Filtra per sistema" },
                  { icon: Play, text: "Guarda nel gestionale" }, { icon: Heart, text: "Salva preferiti" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-zinc-500 text-xs"><item.icon className="w-4 h-4 text-red-400" /><span>{item.text}</span></div>
                ))}
              </div>
            </CardContent>
          </Card>
          {recentVideos.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2"><Clock className="w-5 h-5 text-zinc-400" /> Video Recenti dai Canali Tecnici</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="recent-videos-grid">
                {recentVideos.map(v => (
                  <VideoCard key={v.video_id} video={v} onPlay={setPlayingVideo} isFavorite={favoriteIds.has(v.video_id)} onToggleFavorite={toggleFavorite} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Player modal */}
      {playingVideo && (
        <VideoPlayer video={playingVideo} onClose={() => setPlayingVideo(null)} isFavorite={favoriteIds.has(playingVideo.video_id)} onToggleFavorite={toggleFavorite} />
      )}

      {/* Channel Manager modal */}
      {showChannelManager && (
        <ChannelManager isAdmin={isAdmin} onClose={() => setShowChannelManager(false)} />
      )}
    </div>
  );
}
